import {
  Application,
  Container,
  Graphics,
  NineSliceSprite,
  Sprite,
  Text,
  Texture,
} from 'pixi.js'

import type { TumbleSpin } from '../api/client'
import { TUMBLE_TILE, boardLayout, tileFor } from '../config'
import { log } from '../log'
import type { BoardLayout } from '../config'
import {
  BACKGROUND_KEY,
  BOMB_KEY,
  DEFAULT_BANNERS as BANNER_FALLBACK,
  bannerKey,
  DEFAULT_BOMB as BOMB_FALLBACK,
  DEFAULT_TUMBLE_TIMING,
  FRAME_KEY,
  backgroundTextureFor,
  loadThemeAssets,
  textureFor,
} from '../theme'
import type { BannerKind, ResolvedTheme, TumbleTiming } from '../theme'
import { playSfx } from '../audio/sfx'
import { coverBoard, fillCell, fitArt, fitFrame } from './art'
import { driveWhileHidden } from './backgroundClock'
import { ParticleField } from './particles'
import { generatedPalette, styleFrom } from './symbols'
import type { GameSymbol, SymbolStyle } from './symbols'

/**
 * The board of a tumble game on PixiJS.
 *
 * The client computes NOTHING: the grid of every frame, what won, what
 * will explode, where the survivors settle — all of it is fixed by the
 * RoundResult contract. This class only animates the transitions between
 * states that are already known:
 *
 *   frames[f].grid ──(highlight wins → explode → settle → refill)──▶ frames[f+1].grid
 *
 * Settling is not "simulated with physics": the surviving cells of a
 * column in frame f are the bottom cells of that column in frame f+1, in
 * the same order, so every tile's target is known exactly. The new tiles
 * are the top cells of f+1.
 *
 * The same principle as in PixiSlot: an imperative layer that React does
 * not reach into. The exchange is one-way — the methods return promises.
 *
 * Grid geometry comes from the game description: the lobby may hold
 * several tumble games of different sizes, and 6×5 is nowhere hardcoded.
 *
 * Look comes from the theme (`ResolvedTheme`): colours, labels, sprites,
 * font, timings. This class knows no game by name — it plays the contract
 * in whatever dress it was handed. A new theme requires no edit here; see
 * `docs/theme-pipeline.md`.
 */

/** What the board needs to know about a game: how many cells, which symbols. */
export interface TumbleGameView {
  gameId: string
  layout: { reels: number; rows: number }
  symbols: GameSymbol[]
}

interface Tile {
  view: Container
  bg: Graphics
  /** The symbol's own backdrop art, under `art`. Hidden without one. */
  bgArt: Sprite
  /** Additive halo under a multiplier. Hidden for every other symbol. */
  halo: Sprite
  /** The symbol's own silhouette, offset and darkened, drawn under `art`. */
  shadow: Sprite
  /** Symbol art. Empty until a theme supplies a frame for this code. */
  art: Sprite
  label: Text
  /** ≥0 — symbol id, <0 — a bomb with multiplier −value. */
  value: number
}

/**
 * The multiplier's halo, built once and shared by every tile.
 *
 * A radial gradient painted into a canvas rather than a shipped PNG: it is
 * four colour stops, it has to match the palette exactly, and an asset would
 * be one more file to keep in step with it. Drawn with additive blending, so
 * it lights the plate underneath instead of covering it — which is the whole
 * point. A multiplier is the one symbol that changes what a spin is worth,
 * and a second painted backdrop under an already dense grid reads as clutter
 * where light reads as importance.
 */
let haloTexture: Texture | null = null

function multiplierHalo(): Texture {
  if (haloTexture) return haloTexture
  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return Texture.WHITE
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  // Gold at the core into red at the rim: the same ladder the medallion
  // itself is painted on, so the light looks like it comes off the object.
  g.addColorStop(0, 'rgba(255, 240, 200, 0.95)')
  g.addColorStop(0.26, 'rgba(255, 170, 60, 0.72)')
  g.addColorStop(0.56, 'rgba(224, 62, 30, 0.3)')
  g.addColorStop(1, 'rgba(150, 18, 8, 0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  haloTexture = Texture.from(canvas)
  return haloTexture
}

/**
 * How far a multiplier leans, in radians. Every one leans the same way on
 * purpose: varying the angle per cell reads as a layout accident rather than
 * as a deliberate badge.
 */
const BOMB_TILT = (20 * Math.PI) / 180

/**
 * Banner type, in board units. Fixed rather than tiered by string length:
 * the plaque grows to fit, so the face no longer has to shrink to make room,
 * and a line that changed size between events read as two different banners.
 */
const BANNER_FONT_SIZE = 46
/** Cap height of the display face, as a share of its size. Measured. */
const BANNER_CAP_HEIGHT = 0.75
/** How much of the field's height the capitals take. */
const BANNER_FIELD_FILL = 0.62
/** Clearance from the cap's inner edge to the text, in image pixels. */
const BANNER_TEXT_PAD = 46
/** Shadow offset, in board units. */
const BANNER_SHADOW = 1.2

type Ease = (p: number) => number
const easeOutCubic: Ease = (p) => 1 - (1 - p) ** 3
const easeInCubic: Ease = (p) => p ** 3
const easeOutBack: Ease = (p) => {
  const c1 = 1.4
  return 1 + (c1 + 1) * (p - 1) ** 3 + c1 * (p - 1) ** 2
}
/** Legacy themes keep their original slight bounce unless they opt into a separate landing phase. */
const easeOutBounceSoft: Ease = (p) => {
  if (p < 0.82) return easeOutCubic(p / 0.82)
  const q = (p - 0.82) / 0.18
  return 1 + Math.sin(q * Math.PI) * 0.035 * (1 - q)
}
/**
 * The symbol whose pop is solar rather than airborne. A code, not an id:
 * ids are assigned by the math release and mean nothing to a renderer,
 * while the code is the theme's own name for the bird.
 */
const SUN_CODE = 'H1'

/**
 * The two palettes a pop can be made of, hottest first.
 *
 * Ramps rather than single tints because a one-colour burst reads as a
 * coloured dot however big it is. Sampling each particle from a ramp gives
 * the cloud a temperature gradient — white at the core, deep at the rim —
 * and since particles die at different times the gradient keeps sliding
 * while the burst lives. That sliding IS the shimmer; nothing animates a
 * sprite's tint.
 */
const SUN_RAMP = [0xfff8e0, 0xffe08a, 0xffab2e, 0xff6a12, 0xd8341a]
const WIND_RAMP = [0xffffff, 0xe8f8ff, 0xa8e4ff, 0x6ec1f0, 0x3d8fd0]

/** Sample a ramp at 0..1, blending between the two nearest stops. */
function rampAt(ramp: number[], t: number): number {
  const clamped = Math.min(0.9999, Math.max(0, t))
  const pos = clamped * (ramp.length - 1)
  const i = Math.floor(pos)
  return mixColor(ramp[i], ramp[i + 1] ?? ramp[i], pos - i)
}

/** Linear blend of two packed RGB values. `t` 0 = a, 1 = b. */
function mixColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff
  const ag = (a >> 8) & 0xff
  const ab = a & 0xff
  const br = (b >> 16) & 0xff
  const bg = (b >> 8) & 0xff
  const bb = b & 0xff
  const r = Math.round(ar + (br - ar) * t)
  const g = Math.round(ag + (bg - ag) * t)
  const bch = Math.round(ab + (bb - ab) * t)
  return (r << 16) | (g << 8) | bch
}

interface Tween {
  elapsed: number
  duration: number
  delay: number
  ease: Ease
  apply: (p: number) => void
  resolve: () => void
}

export interface TumbleSpinCallbacks {
  /** The chain's win, accumulated after every winning frame (reference coins). */
  onFrameWin?: (tumbleWinSoFar: number) => void
  /** After the bombs are applied: their sum and the chain's total. */
  onBombs?: (bombSum: number, chainTotal: number) => void
}

export class PixiTumble {
  private app: Application | null = null
  /** Keeps the render resolution matched to the displayed size. */
  private sizeWatch: ResizeObserver | null = null
  /** Unregisters this app from the hidden-window clock. */
  private unclock: (() => void) | null = null
  /**
   * `sortableChildren` because a multiplier is drawn larger than its cell and
   * leans over the tiles beside it: it has to paint after them whatever order
   * the grid was built in, and `zIndex` on the tile is what decides that.
   */
  private board = Object.assign(new Container(), { sortableChildren: true })
  /** Additive effects: over the cells, under the frame and the banners. */
  private fx = new ParticleField()
  /**
   * The rays of the solar pop. Its own field because a field carries one
   * texture, and the star is the only shape on the board that is not a
   * round blob. Additive, like `fx`: these are light, not objects, and
   * light is what sums towards white where it overlaps.
   */
  private fxStar = new ParticleField('add', 'star')
  private overlay = new Container()
  /** tiles[reel][row] — the tiles currently on screen. */
  private tiles: Tile[][] = []
  private tweens: Tween[] = []
  private styles: Record<string, SymbolStyle> = {}
  private codeById = new Map<number, string>()
  private scatterId = -1
  private busy = false
  /** Free-running clock for the multiplier halo, ms. */
  private haloPhase = 0
  /** Board geometry of this game. Set in init before the first paint. */
  private L: BoardLayout = boardLayout(6, 5, TUMBLE_TILE)
  /** Margin between the grid and the canvas edge, px. See `gridPadding`. */
  private pad = 0
  /** Styling. Null before init, so the class stays constructible. */
  private theme: ResolvedTheme | null = null
  private T: TumbleTiming = DEFAULT_TUMBLE_TIMING
  /** Playback rate of the board's clock. 1 = the theme's own timings. */
  private speed = 1

  /**
   * Turbo. Applied to the clock rather than to the timings, so a round that
   * is already in flight keeps its staggers and its particles in step.
   */
  setSpeed(rate: number): void {
    this.speed = rate > 0 ? rate : 1
  }

  async init(host: HTMLElement, game: TumbleGameView, theme?: ResolvedTheme): Promise<void> {
    this.theme = theme ?? null
    this.T = theme?.timings.tumble ?? DEFAULT_TUMBLE_TIMING
    const { tile, max } = tileFor(TUMBLE_TILE, theme?.board)
    this.L = boardLayout(game.layout.reels, game.layout.rows, tile, max)
    this.styles = theme?.symbols ?? generatedPalette(game.symbols)
    // Art loads before the first paint: otherwise frame one would show
    // tiles and frame two sprites, and the board would blink on round start.
    if (theme) await loadThemeAssets(theme)
    for (const s of game.symbols) {
      this.codeById.set(s.id, s.code)
      if (s.kind === 'scatter') this.scatterId = s.id
    }

    const L = this.L
    // The canvas is the grid plus a quiet margin on every side. That margin
    // is where a dropping symbol becomes visible: it is already drawn there
    // while the frame art still covers it, so it slides out from under the
    // frame instead of appearing sliced against the canvas edge.
    const pad = this.theme?.board.gridPadding ?? 0
    this.pad = pad
    const canvasWidth = L.boardWidth + pad * 2
    const canvasHeight = L.boardHeight + pad * 2
    const app = new Application()
    await app.init({
      width: canvasWidth,
      height: canvasHeight,
      // A theme may supply its own backdrop; without one the page shows through.
      ...(this.theme?.board.background != null
        ? { background: this.theme.board.background, backgroundAlpha: 1 }
        : { backgroundAlpha: 0 }),
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    })
    host.appendChild(app.canvas)
    this.app = app
    // A hidden window gets no rAF, and the round pacing in the store does
    // not stop with it. Without this the board freezes mid-round.
    this.unclock = driveWhileHidden(app)

    // The canvas renders at a fixed logical size, so any layout that shows
    // it larger — fullscreen most of all — stretches the backing store and
    // the art goes soft. Track the size it is actually displayed at and
    // raise the renderer's resolution to match, capped so a wide screen
    // cannot ask for a texture the GPU refuses. The host is observed rather
    // than the canvas: Pixi writes the canvas's own style, and watching
    // what you also write is how a resize loop starts.
    const matchResolution = (): void => {
      const shown = host.getBoundingClientRect().width
      if (shown <= 0) return
      const dpr = window.devicePixelRatio || 1
      // Snap up to a whole multiple of the logical size. At a fractional
      // resolution every cell lands on its own subpixel phase — 1.3275 in
      // fullscreen puts the six columns on .82 .19 .57 .94 .31 .69 of a
      // pixel — and identical symbols come out looking different. A whole
      // multiple puts every cell on exact backing-store pixels, and the
      // browser's own downscale to the displayed size is then one uniform
      // filter over the whole canvas instead of thirty different phases.
      const needed = Math.max(dpr, (shown / canvasWidth) * dpr)
      const wanted = Math.min(3, Math.ceil(needed))
      if (Math.abs(wanted - app.renderer.resolution) < 0.05) return
      app.renderer.resize(canvasWidth, canvasHeight, wanted)
    }
    matchResolution()
    this.sizeWatch = new ResizeObserver(matchResolution)
    this.sizeWatch.observe(host)

    // Layer order is the whole visual structure: backdrop, cells, frame,
    // banners. The backdrop sits OUTSIDE the masked board container — the
    // mask exists to hide falling tiles, and a backdrop clipped by it
    // would disappear the moment the board is smaller than its art.
    this.addBackdrop(app.stage, L)
    // The particle layer is a sibling of the board, not a child: it must not
    // inherit the board's mask, and the frame in `overlay` is what trims the
    // sparks that fly past the edge.
    // Swirls under the glows, rays over them: a ray crossed by a swirl
    // reads as a mistake, a swirl crossed by a ray reads as depth.
    app.stage.addChild(this.board, this.fx.view, this.fxStar.view, this.overlay)

    // Mask: the whole canvas, padding included. The padding is deliberately
    // inside the mask — that is the strip a falling tile crosses while the
    // frame hides it. The canvas edge is what finally clips the fall.
    const mask = new Graphics().rect(0, 0, canvasWidth, canvasHeight).fill(0xffffff)
    app.stage.addChild(mask)
    this.board.mask = mask

    for (let r = 0; r < L.reels; r++) {
      const col: Tile[] = []
      for (let row = 0; row < L.rows; row++) {
        const tile = this.createTile(this.placeholderValue())
        tile.view.x = this.cx(r)
        tile.view.y = this.cy(row)
        this.board.addChild(tile.view)
        col.push(tile)
      }
      this.tiles.push(col)
    }

    // The frame goes on last: it is drawn over the cells by definition, and
    // it must stay under the banners, which have to be readable over it.
    this.addFrame(L)

    app.ticker.add((ticker) => this.update(ticker.deltaMS))
  }

  /** Board backdrop, if the theme ships one. Below every cell. */
  private addBackdrop(stage: Container, L: BoardLayout): void {
    const texture = this.theme ? textureFor(this.theme, BACKGROUND_KEY) : null
    if (!texture) return
    const sprite = new Sprite(texture)
    sprite.anchor.set(0.5)
    // Covers the padding too: the backdrop IS the quiet margin the player
    // sees around the grid, not just the ground under the cells.
    coverBoard(sprite, texture, L.boardWidth + this.pad * 2, L.boardHeight + this.pad * 2)
    stage.addChild(sprite)
  }

  /** Frame art over the cells: the portal, the cabinet, the carved edge. */
  private addFrame(L: BoardLayout): void {
    // Inset art is a true surround rendered by OuterBoardFrame outside this
    // board-sized canvas. Drawing it here would clip the overscan and leave
    // the rails sitting on top of the outside symbols.
    if (this.theme?.board.frameInset) return
    const texture = this.theme ? textureFor(this.theme, FRAME_KEY) : null
    if (!texture) return
    const sprite = new Sprite(texture)
    sprite.anchor.set(0.5)
    fitFrame(sprite, texture, L.boardWidth, L.boardHeight, this.theme?.board.frameInset ?? null)
    // Frame art is decoration: it must never eat a click meant for the board.
    sprite.eventMode = 'none'
    this.overlay.addChild(sprite)
  }

  private placeholderValue(): number {
    const ids = [...this.codeById.keys()].filter((id) => id !== this.scatterId)
    return ids[Math.floor(Math.random() * ids.length)] ?? 0
  }

  // ── Tiles ──

  private styleOf(value: number): SymbolStyle {
    if (value < 0) return this.theme?.bomb ?? BOMB_FALLBACK
    return styleFrom(this.styles, this.codeById.get(value) ?? '?')
  }

  /** Art frame for a cell value, or null — then we draw a coloured tile. */
  private textureOf(value: number): ReturnType<typeof textureFor> {
    if (!this.theme) return null
    if (value < 0) return textureFor(this.theme, BOMB_KEY)
    return textureFor(this.theme, this.codeById.get(value) ?? '?')
  }

  /** The cell's own backdrop art, or null — then the flat `fill` tile. */
  private backgroundOf(value: number): ReturnType<typeof textureFor> {
    if (!this.theme) return null
    if (value < 0) return backgroundTextureFor(this.theme, BOMB_KEY)
    return backgroundTextureFor(this.theme, this.codeById.get(value) ?? '?')
  }

  private createTile(value: number): Tile {
    const L = this.L
    const font = this.theme?.font
    const view = new Container()
    const bg = new Graphics()
    // Snap the art layers to whole device pixels. The canvas renders at a
    // logical size and is shown at whatever the layout gives it, so the
    // logical-to-device factor is never a round number — at 664 logical on
    // 820 shown it is 1.2349, and the six columns land on .82 .19 .57 .94
    // .31 .69 of a pixel. Each cell then samples the texture at its own
    // subpixel phase, which is why two copies of one symbol in a single
    // spin come out looking different. Rounding costs nothing at rest and
    // quantises the fall by well under a logical pixel.
    const bgArt = new Sprite()
    bgArt.anchor.set(0.5)
    bgArt.roundPixels = true
    bgArt.x = L.symbolSize / 2
    bgArt.y = L.symbolSize / 2
    bgArt.visible = false
    // The symbol's own silhouette, darkened and pushed down-right, so the
    // character sits ON its backdrop instead of floating over it. A hard
    // offset rather than a blur: these symbols are flat cartoon art with a
    // solid contour and stepped shading, and a soft photographic shadow
    // reads as a different medium. It is also a plain sprite, so thirty of
    // them cost thirty quads instead of thirty filter passes.
    //
    // Baked into the PNGs this would be wrong — docs/theme-art-spec.md
    // requires the symbol files to ship without a drop shadow, because the
    // same art is drawn at several sizes and over different backdrops.
    const halo = new Sprite(multiplierHalo())
    halo.anchor.set(0.5)
    halo.blendMode = 'add'
    halo.x = L.symbolSize / 2
    halo.y = L.symbolSize / 2
    halo.visible = false
    const shadow = new Sprite()
    shadow.anchor.set(0.5)
    shadow.roundPixels = true
    shadow.tint = 0x2a1608
    shadow.alpha = 0.28
    shadow.visible = false
    const art = new Sprite()
    art.anchor.set(0.5)
    art.roundPixels = true
    art.x = L.symbolSize / 2
    art.y = L.symbolSize / 2
    art.visible = false
    const label = new Text({
      text: '',
      style: {
        fontFamily: font?.family ?? 'Inter, Segoe UI, system-ui, sans-serif',
        fontSize: 20,
        fontWeight: (font?.weight ?? '800') as '800',
        fill: 0xffffff,
        letterSpacing: font?.letterSpacing ?? 0.5,
        align: 'center',
      },
    })
    label.anchor.set(0.5)
    label.x = L.symbolSize / 2
    label.y = L.symbolSize / 2
    // Layer order: flat fill → symbol backdrop → character → label. Both
    // art layers are children of the tile, so the drop, the highlight
    // scale and the explosion fade carry them together.
    view.addChild(bg, halo, bgArt, shadow, art, label)
    // The pivot is the centre, so that scaling on highlight/explosion goes
    // out from the centre. The tile position is set via cx()/cy() — the
    // centre of the cell.
    view.pivot.set(L.symbolSize / 2, L.symbolSize / 2)
    const tile: Tile = { view, bg, bgArt, halo, shadow, art, label, value }
    this.paint(tile, value)
    return tile
  }

  private paint(tile: Tile, value: number): void {
    const L = this.L
    const s = this.styleOf(value)
    const radius = this.theme?.board.tileRadius ?? 16
    tile.value = value
    tile.bg.clear()

    // A multiplier is pinned at an angle and rides above its neighbours;
    // every other symbol sits square in its cell. Set both here rather than
    // only in the bomb branch, so a cell that held a multiplier last cascade
    // does not keep the tilt or the raised z-order.
    const isBomb = value < 0
    // The halo is sized in cells, not pixels, so it survives a cellSize
    // change; it reaches well past the tile because light that stops at the
    // cell edge reads as a square panel, which is the thing we are avoiding.
    tile.halo.visible = isBomb
    const tilt = isBomb ? BOMB_TILT : 0
    tile.view.zIndex = isBomb ? 1 : 0
    tile.art.rotation = tilt
    tile.shadow.rotation = tilt
    tile.label.rotation = tilt

    // Layer 1 — the symbol's own backdrop, stretched over the whole cell.
    // Absent or unloadable ⇒ the flat tile below stands in for it.
    const bgTexture = this.backgroundOf(value)
    tile.bgArt.visible = bgTexture !== null
    if (bgTexture) {
      tile.bgArt.texture = bgTexture
      fillCell(tile.bgArt, L.symbolSize)
    }

    // Layer 2 — the character. Present art wins; the captioned tile stays
    // the default path for symbols the artist has not drawn yet, and it
    // shows on top of the backdrop when only that one exists.
    const texture = this.textureOf(value)
    if (texture) {
      tile.art.texture = texture
      tile.art.visible = true
      if (this.theme) fitArt(tile.art, texture, L.symbolSize, this.theme, s.scale)
      // The shadow tracks the art: same texture, same fitted size, offset by
      // a fraction of the cell so it holds at any cellSize. Down and right,
      // because every other lit surface in these themes is lit from the
      // upper left.
      tile.shadow.texture = texture
      tile.shadow.visible = true
      tile.shadow.setSize(tile.art.width, tile.art.height)
      tile.shadow.x = tile.art.x + L.symbolSize * 0.03
      tile.shadow.y = tile.art.y + L.symbolSize * 0.04
      // A declared backdrop that failed to load falls back to the fill, so
      // the character does not end up floating over the board backdrop.
      if (!bgTexture && s.backgroundImage) {
        tile.bg.roundRect(0, 0, L.symbolSize, L.symbolSize, radius).fill(s.fill)
      }
      // The bomb keeps its number over the art — the multiplier is the
      // whole point of that symbol and it is different every time.
      tile.label.visible = value < 0
      if (value < 0) {
        tile.label.text = `×${-value}`
        // The number rides the medallion, so it grows with it: a bomb drawn
        // at 1.35 with a 26px figure would read as a big coin with small
        // type rather than as one object.
        const grow = s.scale ?? 1
        tile.label.style.fontSize = Math.round((-value >= 100 ? 22 : 26) * grow)
        tile.label.style.fill = s.text
      }
      return
    }
    tile.art.visible = false
    tile.shadow.visible = false
    tile.label.visible = true

    if (value < 0) {
      // Bomb: a dark disc with a golden rim, the number is the multiplier.
      // The disc is skipped when the bomb brings its own backdrop art.
      if (!bgTexture) {
        tile.bg
          .roundRect(0, 0, L.symbolSize, L.symbolSize, radius)
          .fill(0x1f2937)
          .circle(L.symbolSize / 2, L.symbolSize / 2, L.symbolSize * 0.38)
          .fill(s.fill)
          .circle(L.symbolSize / 2, L.symbolSize / 2, L.symbolSize * 0.38)
          .stroke({ color: s.text, width: 3 })
      }
      tile.label.text = `×${-value}`
      tile.label.style.fontSize = -value >= 100 ? 22 : 26
    } else {
      if (!bgTexture) tile.bg.roundRect(0, 0, L.symbolSize, L.symbolSize, radius).fill(s.fill)
      if (value === this.scatterId) {
        const accent = this.theme?.banners.free.fill ?? 0xe0457b
        tile.bg
          .roundRect(3, 3, L.symbolSize - 6, L.symbolSize - 6, Math.max(0, radius - 3))
          .stroke({ color: accent, width: 3 })
      }
      tile.label.text = s.label
      tile.label.style.fontSize = s.fontSize
    }
    tile.label.style.fill = s.text
  }

  /** Coordinates of a cell's centre (accounting for the centred pivot). */
  private cx(reel: number): number {
    return this.pad + reel * this.L.pitch + this.L.symbolSize / 2
  }
  private cy(row: number): number {
    return this.pad + row * this.L.pitch + this.L.symbolSize / 2
  }

  // ── Tweens ──

  private tween(duration: number, delay: number, ease: Ease, apply: (p: number) => void): Promise<void> {
    return new Promise((resolve) => {
      this.tweens.push({ elapsed: 0, duration, delay, ease, apply, resolve })
    })
  }

  private update(realDtMs: number): void {
    // Turbo is a clock, not a set of shorter timings. Scaling the board's
    // own time is the only way the SAME animation plays faster: cutting
    // durations one by one would drift out of step with the staggers and
    // the particle field, which is how a "fast" mode ends up looking like
    // a different, broken one.
    const dtMs = realDtMs * this.speed
    // The multiplier halo breathes. Driven here rather than by a tween
    // because it has no beginning and no end: it lives as long as the
    // symbol is on the board, and a tween would have to be cancelled and
    // restarted on every cascade.
    this.haloPhase += dtMs
    const beat = 0.5 + 0.5 * Math.sin(this.haloPhase / 620)
    // Scale straight from the texture rather than through setSize: the
    // pulse writes scale every frame, and a width/height set in paint would
    // be overwritten by the first beat. 2.1 cells across, so the light
    // reaches past the tile instead of stopping at its edge like a panel.
    const haloScale = ((this.L.symbolSize * 2.1) / 256) * (1 + 0.07 * beat)
    for (const col of this.tiles) {
      for (const tile of col) {
        if (!tile.halo.visible) continue
        tile.halo.alpha = 0.55 + 0.3 * beat
        tile.halo.scale.set(haloScale)
      }
    }

    // Particles outlive the tween that spawned them — they are advanced
    // before the early return, or a burst would freeze mid-air.
    this.fx.update(dtMs)
    this.fxStar.update(dtMs)
    if (this.tweens.length === 0) return
    const done: Tween[] = []
    for (const tw of this.tweens) {
      tw.elapsed += dtMs
      const t = tw.elapsed - tw.delay
      if (t < 0) continue
      const p = Math.min(1, t / tw.duration)
      tw.apply(tw.ease(p))
      if (p >= 1) done.push(tw)
    }
    if (done.length) {
      this.tweens = this.tweens.filter((tw) => !done.includes(tw))
      for (const tw of done) tw.resolve()
    }
  }

  // ── Public API ──

  /**
   * Event banner over the board: free-spin trigger, retrigger, summary.
   * Holds for holdMs and disappears. It does not block the tiles — it is
   * an overlay.
   */
  async banner(text: string, kind: BannerKind = 'free', holdMs = 1100): Promise<void> {
    // The banner's own voice: the trigger gets its fanfare, the summary a
    // win hit. Bomb banners stay silent here — celebrateBombs speaks.
    if (kind === 'free') playSfx('free-intro', 0.8)
    else if (kind === 'win') playSfx('win-big', 0.7)
    const b = this.makeBanner(text, kind)
    b.alpha = 0
    this.overlay.addChild(b)
    await this.tween(holdMs + 320, 0, (p) => p, (p) => {
      const inP = Math.min(1, p * (holdMs + 320) / 220)
      const outStart = holdMs / (holdMs + 320)
      const outP = p > outStart ? (p - outStart) / (1 - outStart) : 0
      b.scale.set(0.7 + 0.3 * easeOutBack(inP))
      b.alpha = Math.min(inP * 1.5, 1) * (1 - outP)
    })
    this.overlay.removeChild(b)
    b.destroy({ children: true })
  }

  /** Show a grid with no animation — the initial state. */
  showGrid(grid: number[]): void {
    const L = this.L
    for (let r = 0; r < L.reels; r++) {
      for (let row = 0; row < L.rows; row++) {
        const tile = this.tiles[r][row]
        this.paint(tile, grid[r * L.rows + row])
        tile.view.x = this.cx(r)
        tile.view.y = this.cy(row)
        tile.view.scale.set(1)
        tile.view.alpha = 1
      }
    }
  }

  /**
   * Play one chain (a base spin or a free spin) per the contract.
   * Returns a promise that resolves once every animation is finished.
   */
  async playSpin(spin: TumbleSpin, cb: TumbleSpinCallbacks = {}): Promise<void> {
    if (this.busy) return
    this.busy = true
    try {
      await this.dropAll(spin.frames[0].grid)

      let tumbleWin = 0
      for (let f = 0; f < spin.frames.length - 1; f++) {
        const frame = spin.frames[f]
        const next = spin.frames[f + 1]
        if (frame.win <= 0) break

        const winningIds = new Set(frame.wins.map((w) => w.symbol))
        await this.highlight(winningIds)
        tumbleWin += frame.win
        cb.onFrameWin?.(tumbleWin)
        // The classic ascending run: one pop file, each cascade of the
        // chain a semitone higher (same trick as the lines win cycle).
        // Capped an octave up — past that the chime turns into a squeak.
        playSfx('cascade-pop', 0.6, 2 ** (Math.min(f, 12) / 12))
        await this.explodeAndRefill(winningIds, next.grid)
      }

      const last = spin.frames[spin.frames.length - 1]
      // The multiplier that actually applied: bombs on screen (Sweet Bonanza
      // class, free spins only) or the persistent global accumulator (Gates
      // class, where bombs may also drop in the base chain). Contract rule:
      // multApplied when present, otherwise bombSum.
      const mult = spin.multApplied ?? spin.bombSum
      if (spin.tumbleWin > 0 && mult > 0) {
        const persistent = spin.multApplied !== undefined && spin.kind === 'free'
        await this.celebrateBombs(last.grid, mult, persistent ? spin.bombSum : 0)
        cb.onBombs?.(mult, spin.tumbleWin * mult)
      }
      if (spin.scatterCount >= 3) {
        await this.pulseScatters(last.grid)
      }
    } finally {
      this.busy = false
    }
  }

  // ── Phases ──

  /**
   * Full board transition: anticipation → old grid exits → empty beat →
   * new grid falls in. Keeping these as distinct phases is what makes a
   * spin read as motion instead of an instantaneous matrix replacement.
   */
  private async dropAll(grid: number[]): Promise<void> {
    const L = this.L

    if (this.T.spinAnticipationMs > 0) {
      const anticipation: Promise<void>[] = []
      for (const col of this.tiles) {
        for (const tile of col) {
          anticipation.push(
            this.tween(this.T.spinAnticipationMs, 0, easeInCubic, (p) => {
              const s = 1 - 0.018 * p
              tile.view.scale.set(s)
            }),
          )
        }
      }
      await Promise.all(anticipation)
    }

    if (this.T.spinExitMs > 0) {
      const outgoing: Promise<void>[] = []
      for (let r = 0; r < L.reels; r++) {
        for (let row = 0; row < L.rows; row++) {
          const tile = this.tiles[r][row]
          const startY = tile.view.y
          const targetY = startY + L.boardHeight + L.pitch * 1.5
          const delay =
            r * this.T.spinExitStaggerMs +
            (L.rows - 1 - row) * this.T.spinExitRowStaggerMs
          outgoing.push(
            this.tween(this.T.spinExitMs, delay, easeInCubic, (p) => {
              tile.view.y = startY + (targetY - startY) * p
              tile.view.scale.set(0.982 + 0.018 * p)
            }),
          )
        }
      }
      await Promise.all(outgoing)
    }

    if (this.T.emptyHoldMs > 0) {
      await this.tween(this.T.emptyHoldMs, 0, (p) => p, () => undefined)
    }

    const promises: Promise<void>[] = []
    for (let r = 0; r < L.reels; r++) {
      for (let row = 0; row < L.rows; row++) {
        const tile = this.tiles[r][row]
        this.paint(tile, grid[r * L.rows + row])
        tile.view.scale.set(1)
        tile.view.alpha = 1
        const targetY = this.cy(row)
        // A deterministic distance keeps neighbouring tiles in one readable
        // wave. Random start heights made the board look like a layout glitch.
        const depthOffset = ((r + row) % 3) * 0.12
        const startY = targetY - L.boardHeight - L.pitch * (1 + depthOffset)
        tile.view.x = this.cx(r)
        tile.view.y = startY
        // Bottom ones fall first: from above you see the column "filling up"
        const delay = r * this.T.dropStaggerMs + (L.rows - 1 - row) * this.T.dropRowStaggerMs
        promises.push(this.fallAndLand(tile, startY, targetY, delay))
      }
    }
    await Promise.all(promises)
    playSfx('tile-land', 0.45)
  }

  /** One physical tile: travel first, then a short, controlled impact. */
  private async fallAndLand(
    tile: Tile,
    startY: number,
    targetY: number,
    delay: number,
    travelMs = this.T.dropMs,
    landingMs = this.T.landingMs,
  ): Promise<void> {
    const travelEase = landingMs > 0 ? easeOutCubic : easeOutBounceSoft
    await this.tween(travelMs, delay, travelEase, (p) => {
      tile.view.y = startY + (targetY - startY) * p
    })
    tile.view.y = targetY
    if (landingMs > 0) {
      await this.tween(landingMs, 0, easeOutCubic, (p) => {
        const impact = Math.sin(p * Math.PI)
        tile.view.scale.set(1 + 0.035 * impact, 1 - 0.05 * impact)
        tile.view.y = targetY + 3 * impact
      })
    }
    tile.view.y = targetY
    tile.view.scale.set(1)

    // A multiplier announces itself. It lands small and over-rotated, then
    // spins into its tilt and overshoots its size before settling — the same
    // easing the tile drop uses, so the two read as one physics. Nothing else
    // on the board does this, which is the point: the multiplier is the one
    // symbol whose arrival changes what the spin is worth.
    if (tile.value < 0 && this.T.bombEntranceMs > 0) {
      const from = BOMB_TILT - Math.PI * 0.8
      await this.tween(this.T.bombEntranceMs, 0, easeOutBack, (p) => {
        tile.view.scale.set(0.42 + 0.58 * p)
        const angle = from + (BOMB_TILT - from) * p
        tile.art.rotation = angle
        tile.shadow.rotation = angle
        tile.label.rotation = angle
      })
      tile.view.scale.set(1)
      tile.art.rotation = BOMB_TILT
      tile.shadow.rotation = BOMB_TILT
      tile.label.rotation = BOMB_TILT
    }
  }

  /** Winning symbols pulse, the rest dim a little. */
  private async highlight(winningIds: Set<number>): Promise<void> {
    const promises: Promise<void>[] = []
    for (const col of this.tiles) {
      for (const tile of col) {
        const isWin = tile.value >= 0 && winningIds.has(tile.value)
        promises.push(
          this.tween(this.T.highlightMs, 0, (p) => p, (p) => {
            if (isWin) {
              const s = 1 + 0.1 * Math.sin(p * Math.PI * 2)
              tile.view.scale.set(s)
              tile.view.alpha = 1
            } else {
              tile.view.alpha = 1 - 0.35 * Math.sin(p * Math.PI)
            }
          }),
        )
      }
    }
    await Promise.all(promises)
    for (const col of this.tiles) for (const tile of col) tile.view.scale.set(1)
  }

  /**
   * Explode the winners → settle the survivors → pull new ones in from
   * above. Every tile's target is taken from the next grid, not computed.
   */
  /**
   * The phoenix pop: a small sun going off in the cell.
   *
   * Four layers, and the order they arrive in is the whole effect. A core
   * that flashes white and shrinks; a corona of flame licks thrown outward
   * as STREAKS, not dots — a lick is a direction, and direction is what
   * separates fire from confetti; two crossed rays that bloom and go, which
   * is the part that reads as "pompous" because nothing else on the board
   * has spikes; and embers that keep rising after the rest is gone, so the
   * cell does not simply switch off.
   *
   * Every particle takes its tint from `SUN_RAMP` at a random position, so
   * the cloud is a temperature gradient rather than an orange blob.
   */
  private sunPop(x: number, y: number, cell: number, density = 1): void {
    // The flash. Big, brief, hottest stop of the ramp: it covers the frame
    // where the symbol hands over to its replacement, which is the ugliest
    // frame in the cascade if nothing hides it.
    this.fx.emit(x, y, {
      vx: 0,
      vy: 0,
      life: 0.42,
      size: cell * 1.02,
      gravity: 0,
      // Gold, and well under full. Additive layers stack, and a white flash
      // over a gold corona clips the cell to paper white — the colour that
      // was supposed to say "sun" is the first thing a saturated sum loses.
      alpha0: 0.62,
      color: SUN_RAMP[1],
      spin: 0,
      grow: -0.45,
      fade: 'fog',
      drag: 0,
    })

    // The corona. Licks leave along their own radius and are stretched
    // along it, so the ring reads as flame thrown outward rather than as a
    // circle of sparks. Hotter stops go faster: real flame sorts itself
    // that way, and it puts the white at the leading edge.
    const licks = Math.max(8, Math.round(22 * density))
    for (let i = 0; i < licks; i++) {
      const angle = (i / licks) * Math.PI * 2 + (Math.random() - 0.5) * 0.5
      const heat = Math.random()
      const speed = cell * (1.4 + (1 - heat) * 1.6) * (0.75 + Math.random() * 0.5)
      this.fx.emit(x, y, {
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.5 + Math.random() * 0.3,
        size: cell * (0.1 + Math.random() * 0.09),
        gravity: 0,
        alpha0: 0.7 + Math.random() * 0.3,
        color: rampAt(SUN_RAMP, heat * 0.9 + 0.1),
        spin: 0,
        // Long and pointing outward: the glow sprite's own axis is
        // vertical, so the quarter turn is what aims it along the radius.
        stretch: 2.6 + Math.random() * 2.2,
        rotation: angle + Math.PI / 2,
        grow: -0.35,
        fade: 'square',
        drag: 1.9,
      })
    }

    // The rays. Two, offset in angle and in time, so the flare turns
    // slightly as it opens instead of sitting there like a decal.
    for (let i = 0; i < 2; i++) {
      this.fxStar.emit(x, y, {
        vx: 0,
        vy: 0,
        life: 0.58 + i * 0.2,
        size: cell * (2.1 + i * 0.9),
        gravity: 0,
        alpha0: 0.62 - i * 0.28,
        // Deep gold rather than the white end of the ramp: the star texture
        // already carries its own hot centre, so tinting it white twice is
        // what turned the rays into a bleach spot.
        color: rampAt(SUN_RAMP, 0.35 + i * 0.35),
        spin: i === 0 ? 0.9 : -0.6,
        rotation: i * (Math.PI / 5),
        grow: 0.55,
        fade: 'fog',
        drag: 0,
      })
    }

    // Embers. Negative gravity: hot air rises, and the cell keeps glowing
    // for a beat after the replacement has already dropped into it.
    const embers = Math.max(4, Math.round(9 * density))
    for (let i = 0; i < embers; i++) {
      this.fx.emit(x + (Math.random() - 0.5) * cell * 0.7, y + (Math.random() - 0.5) * cell * 0.5, {
        vx: (Math.random() - 0.5) * cell * 0.5,
        vy: -cell * (0.2 + Math.random() * 0.4),
        life: 0.85 + Math.random() * 0.6,
        size: cell * (0.045 + Math.random() * 0.05),
        gravity: -cell * 0.5,
        alpha0: 0.6 + Math.random() * 0.35,
        color: rampAt(SUN_RAMP, 0.35 + Math.random() * 0.6),
        spin: 0,
        grow: -0.5,
        fade: 'fog',
        drag: 0.8,
      })
    }
  }

  /**
   * The wind pop: the symbol is carried off by a gust.
   *
   * A vortex is not a burst. Sparks thrown outward say "this exploded";
   * arcs travelling AROUND a centre while drifting out say "this was blown
   * away", and that is the read the non-phoenix birds want. So the wisps
   * are launched on the tangent, not the radius, with only a small outward
   * component — the spiral comes from the ratio of the two, and dropping
   * the outward part entirely would leave a spinning wheel instead.
   *
   * The symbol's own colour is blended into the pale wind ramp rather than
   * replacing it. Pure symbol colour loses the element; pure wind colour
   * makes every bird's pop identical, and the eye uses that colour to see
   * WHICH symbol just paid.
   */
  private windPop(x: number, y: number, cell: number, color: number, density = 1): void {
    /** Wind first, a third of the bird's own colour on top. */
    const tint = (t: number): number => mixColor(rampAt(WIND_RAMP, t), color, 0.32)

    // The gust core: a wide soft puff, no direction of its own. It is the
    // air the swirls are made of, and without it they read as loose ribbons.
    this.fx.emit(x, y, {
      vx: 0,
      vy: 0,
      life: 0.4,
      size: cell * 0.95,
      gravity: 0,
      alpha0: 0.5,
      color: tint(0.15),
      spin: 0,
      grow: 0.5,
      fade: 'fog',
      drag: 0,
    })

    // Which way this particular whirl turns. Randomised per pop so a board
    // full of them is not a rack of identical cogs.
    const sense = Math.random() < 0.5 ? 1 : -1
    // How tightly the arms coil: radians of lead per cell of radius. Over a
    // turn and a half across the spiral, so the arm visibly wraps the cell
    // instead of sweeping past it.
    const COIL = 7.5

    // The arms.
    //
    // Drawn as a DENSE CHAIN OF SOFT ROUND BLOBS along an Archimedean
    // spiral — not as arc sprites. This is the third attempt and the first
    // one that stops reading as blades, and the reason is that the blade
    // was never the tint or the speed: it was the sprite. Any sprite with
    // a long axis has two ends and a spine, and a row of them turning
    // about a point is a fan of knives no matter how softly it is drawn.
    //
    // A round blob has no axis and no ends, so it cannot be a blade. Spaced
    // much closer together than their own radius they overlap into one
    // continuous stroke, and a continuous stroke with no edges is exactly
    // what a current of air looks like when anyone draws one. The line's
    // shape comes from where the blobs are, never from what any one of them
    // is — which is also why the arm can bend as sharply as it likes.
    const ARMS = 4
    const perArm = Math.max(12, Math.round(26 * density))
    for (let arm = 0; arm < ARMS; arm++) {
      const armBase = (arm / ARMS) * Math.PI * 2
      // Each arm gets its own coil rate and its own drift outward. Three
      // identical arms rotating together read as a rigid pinwheel; three
      // that wind at slightly different rates keep sliding out of step,
      // and air currents are never in step.
      const armCoil = COIL * (0.85 + Math.random() * 0.3)
      const armSpread = 0.55 + Math.random() * 0.22
      for (let i = 0; i < perArm; i++) {
        const f = (i + 0.5) / perArm
        // Radius grows down the arm; the angle leads it, which IS the
        // spiral. The jitter is small — enough to fray the stroke, not
        // enough to break it into separate dots.
        // Radius grows fast at the start. Spacing the blobs evenly along
        // the index bunches them where the spiral is tight and the arc
        // between two steps is short, and the middle saturates into a
        // disc; the power spaces them by roughly equal path length.
        const radius = cell * (0.06 + Math.pow(f, 0.62) * armSpread) * (0.96 + Math.random() * 0.08)
        const angle = armBase + sense * armCoil * (radius / cell) + (Math.random() - 0.5) * 0.1
        // Differential rotation: the inside turns faster than the outside,
        // exactly as in any real vortex. This is what makes the arms wind
        // TIGHTER while they live — a rigid rotation would just carousel
        // the whole shape round without ever curling it, which is the
        // "просто по колу крутяться" the previous version was guilty of.
        const omega = sense * (5.4 / (radius / cell + 0.4)) * (0.9 + Math.random() * 0.2)
        this.fx.emit(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius, {
          // Unused under orbit, but the spec still wants them.
          vx: 0,
          vy: 0,
          // Staggered along the arm: the tip dies first, so the stroke
          // retracts into the centre rather than blinking out whole.
          life: (0.78 - f * 0.2) * (0.9 + Math.random() * 0.2),
          // Wide relative to the spacing between neighbours — that overlap
          // is the whole trick, and shrinking it brings the dots back.
          // Growing outward, not shrinking: the stroke thickens as it
          // leaves, which is what a gust does, and it stops the crowded
          // centre from being the fattest part as well as the densest.
          size: cell * (0.12 + f * 0.16),
          gravity: 0,
          // Low, because twenty of these sum additively along the stroke.
          // Bright per blob would blow the arm out to a flat white worm.
          alpha0: 0.3 + Math.random() * 0.14,
          color: tint(0.05 + f * 0.4 + Math.random() * 0.1),
          spin: 0,
          grow: 0.35,
          fade: 'late',
          orbit: {
            cx: x,
            cy: y,
            omega,
            // Drifting out as it turns: the spiral opens instead of
            // spinning in place, which is how a gust dissipates.
            vr: cell * (0.16 + f * 0.3),
            // Winds down over the life. A vortex still turning at full
            // speed when it fades looks cut off rather than spent.
            spinDown: 0.8,
          },
        })
      }
    }

    // Motes caught in the current — a vortex of pure haze has nothing in it
    // for the eye to track, and without something tracking the rotation the
    // whole thing reads as a fade-out rather than a spin.
    //
    // They orbit too, and faster than the ribbons: specks are what the eye
    // actually follows, so they are the ones that have to visibly go round.
    // Round, not streaked — stretched specks turned into a second set of
    // slashes on top of the ribbons, which is most of what made the gust
    // look like it was cutting the symbol rather than lifting it.
    const specks = Math.max(4, Math.round(11 * density))
    for (let i = 0; i < specks; i++) {
      const angle = Math.random() * Math.PI * 2
      const radius = cell * (0.12 + Math.random() * 0.4)
      this.fx.emit(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius, {
        vx: 0,
        vy: 0,
        life: 0.46 + Math.random() * 0.3,
        size: cell * (0.045 + Math.random() * 0.055),
        gravity: 0,
        alpha0: 0.65 + Math.random() * 0.35,
        color: tint(Math.random() * 0.5),
        spin: 0,
        grow: -0.2,
        fade: 'fog',
        orbit: {
          cx: x,
          cy: y,
          omega: sense * (5.6 / (radius / cell + 0.4)) * (0.85 + Math.random() * 0.35),
          vr: cell * (0.3 + Math.random() * 0.5),
          spinDown: 0.8,
        },
      })
    }
  }

  private async explodeAndRefill(winningIds: Set<number>, next: number[]): Promise<void> {
    const L = this.L
    // 1. Explosion
    //
    // A full-board hit pops thirty cells on one frame. At the effect's full
    // density that is nearly a thousand sprites appearing at once, which is
    // where a mid-range phone drops the frame the player is watching. Past
    // a dozen simultaneous pops the individual bursts have stopped being
    // readable anyway — they have merged into one event — so thinning them
    // costs nothing visible and buys back the frame rate.
    const popping = this.tiles.reduce(
      (n, col) => n + col.filter((t) => t.value >= 0 && winningIds.has(t.value)).length,
      0,
    )
    const density = popping > 12 ? 0.5 : 1
    const explode: Promise<void>[] = []
    for (const col of this.tiles) {
      for (const tile of col) {
        if (tile.value >= 0 && winningIds.has(tile.value)) {
          // Two elements dress this world: the phoenix's fire and the wind
          // the other birds ride. A pop is one or the other, never a
          // generic spark, and the SYMBOL decides — in free spins as much
          // as in the base game. Burning everything during the bonus was
          // tried and it cost more than it bought: the bonus is where the
          // player watches the board longest, and that is exactly where
          // every symbol popped the same way and the elements stopped
          // meaning anything. The bonus is already announced by the light
          // around the cabinet, the badge and the music; it does not need
          // to flatten the one effect that tells symbols apart.
          const solar = tile.value === this.scatterId || this.codeById.get(tile.value) === SUN_CODE
          const color = this.styleOf(tile.value).fill
          const x = tile.view.x
          const y = tile.view.y
          let sparked = false
          explode.push(
            this.tween(this.T.explodeMs, 0, easeInCubic, (p) => {
              tile.view.scale.set(1 + 0.25 * p * (1 - p) * 4 - p)
              tile.view.alpha = 1 - p
              // Fired at the peak of the squash, not at p=0: the symbol is
              // then at its largest and the flash hides the hand-off to the
              // falling replacement.
              if (!sparked && p >= 0.35) {
                sparked = true
                if (solar) this.sunPop(x, y, L.symbolSize, density)
                else this.windPop(x, y, L.symbolSize, color, density)
              }
            }),
          )
        }
      }
    }
    await Promise.all(explode)

    // 2. Rebuild the columns: survivors settle to the bottom (order kept),
    //    the exploded tiles are reassigned to the new top cells and fall.
    const moves: Promise<void>[] = []
    for (let r = 0; r < L.reels; r++) {
      const col = this.tiles[r]
      const survivors = col.filter((t) => !(t.value >= 0 && winningIds.has(t.value)))
      const exploded = col.filter((t) => t.value >= 0 && winningIds.has(t.value))
      const removed = exploded.length
      if (removed === 0) continue

      const newCol: Tile[] = new Array(L.rows)
      // survivors → rows removed..rows-1, in the same order
      survivors.forEach((tile, i) => {
        const row = removed + i
        newCol[row] = tile
        const targetY = this.cy(row)
        const startY = tile.view.y
        if (targetY !== startY) {
          moves.push(
            this.tween(this.T.settleMs, 0, easeOutBack, (p) => {
              tile.view.y = startY + (targetY - startY) * p
            }),
          )
        }
        // Sanity check of the contract: a survivor must match next
        const expected = next[r * L.rows + row]
        if (expected !== tile.value) {
          // Do not crash — repaint per the contract. A mismatch here means
          // a bug in the engine or in the client.
          // Fires per cell inside an animation loop, which is exactly what
          // the logger's flood control exists for.
          log.error('tumble.contract-mismatch', undefined, 'a survivor does not match `next`', {
            reel: r,
            row,
            was: tile.value,
            expected,
          })
          this.paint(tile, expected)
        }
      })
      // new ones → rows 0..removed-1, falling in from above
      exploded.forEach((tile, i) => {
        const row = i
        newCol[row] = tile
        this.paint(tile, next[r * L.rows + row])
        tile.view.scale.set(1)
        tile.view.alpha = 1
        const targetY = this.cy(row)
        const startY = -L.pitch * (removed - row) - L.symbolSize / 2
        tile.view.y = startY
        moves.push(
          this.fallAndLand(
            tile,
            startY,
            targetY,
            25 +
              r * this.T.cascadeColumnStaggerMs +
              (removed - 1 - row) * this.T.cascadeRowStaggerMs,
            this.T.cascadeDropMs,
            this.T.cascadeLandingMs,
          ),
        )
      })
      this.tiles[r] = newCol
    }
    await Promise.all(moves)
    // One soft thud for the whole refill, not one per tile: the board
    // lands as an event, thirty thuds land as gravel.
    playSfx('tile-land', 0.4)
  }

  /**
   * The bombs pulse, and the multiplier shows over the board. For a persistent
   * accumulator the banner also names this spin's contribution ("+12 → ×38"),
   * so the player sees the counter grow rather than a bare number.
   */
  private async celebrateBombs(grid: number[], mult: number, addedNow = 0): Promise<void> {
    const L = this.L
    // No arrow between the two numbers: the display face has no glyph for
    // one, so the browser substituted a different font for that character
    // alone. Order carries the meaning — added, then the new total.
    const text = addedNow > 0 ? `+${addedNow} ×${mult}` : `×${mult}`
    const banner = this.makeBanner(text, 'bomb')
    playSfx('bomb-hit', 0.75)
    this.overlay.addChild(banner)
    const promises: Promise<void>[] = []
    for (let r = 0; r < L.reels; r++) {
      for (let row = 0; row < L.rows; row++) {
        const tile = this.tiles[r][row]
        if (grid[r * L.rows + row] < 0) {
          promises.push(
            this.tween(this.T.bombMs, 0, (p) => p, (p) => {
              tile.view.scale.set(1 + 0.18 * Math.abs(Math.sin(p * Math.PI * 3)))
            }),
          )
        }
      }
    }
    promises.push(
      this.tween(this.T.bombMs, 0, (p) => p, (p) => {
        // Pop, hold, fade. The old envelope spent three quarters of the
        // window growing and then took the plaque away, so the number was
        // at full size for barely a tenth of a second — long enough to
        // notice, too short to read.
        const inP = Math.min(1, p / 0.16)
        const outP = p > 0.88 ? (p - 0.88) / 0.12 : 0
        // Ends at 1, not at the old 1.2: the plaque is already built to the
        // width it needs, and the clamp that keeps it inside the board is
        // applied before this runs — scaling past 1 walked it over the edge.
        banner.scale.set(0.72 + 0.28 * easeOutBack(inP))
        banner.alpha = 1 - outP
      }),
    )
    await Promise.all(promises)
    for (const col of this.tiles) for (const tile of col) tile.view.scale.set(1)
    this.overlay.removeChild(banner)
    banner.destroy({ children: true })
  }

  /** Scatters light up — a trigger or a retrigger. */
  private async pulseScatters(grid: number[]): Promise<void> {
    const L = this.L
    playSfx('scatter-hit', 0.7)
    const promises: Promise<void>[] = []
    for (let r = 0; r < L.reels; r++) {
      for (let row = 0; row < L.rows; row++) {
        if (grid[r * L.rows + row] !== this.scatterId) continue
        const tile = this.tiles[r][row]
        const glow = this.theme?.banners.free.fill ?? 0xe0457b
        // Embers are emitted in bursts along the pulse rather than once, so
        // the scatter keeps smouldering for the whole animation.
        let nextEmber = 0
        promises.push(
          this.tween(this.T.scatterMs, r * 40, (p) => p, (p) => {
            tile.view.scale.set(1 + 0.15 * Math.sin(p * Math.PI))
            tile.view.rotation = 0.12 * Math.sin(p * Math.PI * 2)
            if (p >= nextEmber) {
              nextEmber += 0.2
              this.fx.embers(tile.view.x, tile.view.y, {
                color: glow,
                count: 3,
                size: L.symbolSize * 0.16,
              })
            }
          }),
        )
      }
    }
    await Promise.all(promises)
    for (const col of this.tiles) for (const tile of col) tile.view.rotation = 0
  }

  /**
   * Banner plate. A theme can supply a stretchable plaque; without one the
   * banner is a drawn rectangle, which is what every theme had before.
   */
  private makeBanner(text: string, kind: BannerKind): Container {
    const L = this.L
    const style = this.theme?.banners[kind] ?? BANNER_FALLBACK[kind]
    const plaque = style.image ? (this.theme?.textures.get(bannerKey(kind)) as Texture) : null
    const c = new Container()
    const fontFamily = this.theme?.font.family ?? 'Inter, Segoe UI, system-ui, sans-serif'

    if (plaque && style.cap > 0) {
      // Size runs from the type outward: the letters fill a fixed share of
      // the field, the field is a known fraction of the artwork, and the
      // plaque follows from both. Sizing the plaque first and fitting type
      // into the remainder is what left the line rattling around in it.
      const fieldFrac = (style.fieldBottom - style.fieldTop) / plaque.height
      const plaqueH = ((BANNER_FONT_SIZE * BANNER_CAP_HEIGHT) / BANNER_FIELD_FILL) / fieldFrac
      const k = plaqueH / plaque.height
      const cap = style.cap * k

      const t = new Text({
        text,
        style: { fontFamily, fontSize: BANNER_FONT_SIZE, fontWeight: '900', fill: style.text },
      })
      t.anchor.set(0.5)

      // The ends never stretch, so they set the floor on how short the
      // plaque can be — a two-character line simply sits with more air.
      let width = Math.max(2 * cap + 8 * k, t.width + 2 * (cap + BANNER_TEXT_PAD * k))
      let shrink = 1
      const maxWidth = L.boardWidth - 16
      if (width > maxWidth) {
        // Whole-plaque shrink rather than a smaller face: the caps come down
        // with it, so the proportions hold. Only an eight-figure win on a
        // narrow board gets this far.
        shrink = maxWidth / width
        width = maxWidth
      }
      const height = plaqueH * shrink

      const sprite = new NineSliceSprite({
        texture: plaque,
        leftWidth: style.cap,
        rightWidth: style.cap,
        topHeight: 0,
        bottomHeight: 0,
      })
      // The slice widths are in the texture's own pixels and are NOT scaled
      // with the sprite, so the sprite has to be sized in those pixels too
      // and shrunk afterwards. Sizing it in board units instead drew 197-unit
      // end caps on an 83-unit-tall plaque: the wings came out squashed, the
      // caps ate the whole field, and the line ran out across them.
      const zoom = k * shrink
      sprite.width = width / zoom
      sprite.height = plaque.height
      sprite.scale.set(zoom)
      sprite.x = -width / 2
      sprite.y = -height / 2

      // Centred on the field, not on the plaque: the rail above the field is
      // thinner than the one below it, so the two centres differ.
      const fieldMid = (style.fieldTop + style.fieldBottom) / 2 - plaque.height / 2
      t.y = fieldMid * k * shrink
      t.scale.set(shrink)

      c.addChild(sprite)
      if (style.shadow !== null) {
        const sh = new Text({
          text,
          style: { fontFamily, fontSize: BANNER_FONT_SIZE, fontWeight: '900', fill: style.shadow },
        })
        sh.anchor.set(0.5)
        sh.scale.set(shrink)
        sh.x = BANNER_SHADOW * shrink
        sh.y = t.y + BANNER_SHADOW * 1.5 * shrink
        c.addChild(sh)
      }
      c.addChild(t)
    } else {
      const t = new Text({
        text,
        style: {
          fontFamily,
          // Three tiers rather than two: "FREE SPINS: 1,250,000" is far longer
          // than "×250", and at 40px it would run past the edge of a small
          // board. Length is the only thing we can branch on here — the text
          // is composed by the store and this class must not know its wording.
          fontSize: text.length > 16 ? 32 : text.length > 8 ? 40 : 64,
          fontWeight: '900',
          fill: style.text,
          align: 'center',
        },
      })
      t.anchor.set(0.5)
      const pad = 28
      // Last resort: even the smallest tier can overflow a narrow board, and a
      // banner clipped by the mask reads as a rendering bug. The text is
      // scaled, not the container — the container's scale is animated by the
      // caller and would overwrite anything set here.
      const maxWidth = L.boardWidth - 16
      if (t.width + pad * 2 > maxWidth) {
        t.scale.set(maxWidth / (t.width + pad * 2))
      }
      const bg = new Graphics()
        .roundRect(-t.width / 2 - pad, -t.height / 2 - 12, t.width + pad * 2, t.height + 24, 22)
        .fill({ color: style.fill, alpha: 0.96 })
      c.addChild(bg, t)
    }

    // Centred on the canvas, not the grid: with padding the two differ, and
    // a banner centred on the grid sits visibly off-centre in the frame.
    c.x = (L.boardWidth + this.pad * 2) / 2
    c.y = (L.boardHeight + this.pad * 2) / 2
    return c
  }

  destroy(): void {
    // Resolve every promise, otherwise the store hangs on an await.
    for (const tw of this.tweens) tw.resolve()
    this.tweens = []
    this.busy = false
    // Particles are released before the app tears the stage down, so the
    // pooled sprites do not outlive their renderer.
    this.fx.clear()
    this.fxStar.clear()
    this.sizeWatch?.disconnect()
    this.sizeWatch = null
    this.unclock?.()
    this.unclock = null
    this.app?.destroy(true, { children: true })
    this.app = null
    this.tiles = []
  }
}
